<template>
  <div class="page-content">
    <div class="page-header">
      <div>
        <BreadcrumbComponent :items="items" />
        <h1 class="page-title">Detail du retrait</h1>
      </div>
      <v-btn outlined small color="grey darken-1" @click="$router.push('/moncash/withdrawal-requests')">
        <v-icon small left>mdi-arrow-left</v-icon>
        Retour
      </v-btn>
    </div>

    <PreloaderComponent :enable="enable" />

    <div v-if="enable && withdrawal">
      <!-- Summary cards -->
      <v-row class="mb-4">
        <v-col cols="12" sm="6" md="3">
          <v-card outlined class="summary-card summary-card--total">
            <v-card-text class="pa-4">
              <div class="d-flex align-center mb-1">
                <v-icon color="blue darken-2" small class="mr-2">mdi-cash-multiple</v-icon>
                <span class="summary-label">Montant</span>
              </div>
              <div class="summary-amount">{{ formatCurrency(withdrawal.sale_amount) }}</div>
              <div class="summary-currency">{{ withdrawal.currency }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card outlined class="summary-card summary-card--fees">
            <v-card-text class="pa-4">
              <div class="d-flex align-center mb-1">
                <v-icon color="red darken-1" small class="mr-2">mdi-percent</v-icon>
                <span class="summary-label">Frais</span>
              </div>
              <div class="summary-amount">{{ formatCurrency(withdrawal.withdrawal_fee) }}</div>
              <div class="summary-currency">{{ withdrawal.currency }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card outlined class="summary-card summary-card--receive">
            <v-card-text class="pa-4">
              <div class="d-flex align-center mb-1">
                <v-icon color="green darken-2" small class="mr-2">mdi-cash-check</v-icon>
                <span class="summary-label">A Recevoir</span>
              </div>
              <div class="summary-amount">{{ formatCurrency(withdrawal.amount) }}</div>
              <div class="summary-currency">{{ withdrawal.currency }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card outlined class="summary-card summary-card--status">
            <v-card-text class="pa-4">
              <div class="summary-label mb-2">Statut</div>
              <v-chip small :color="getStatusChipColor(withdrawal.status)" :text-color="getStatusChipTextColor(withdrawal.status)" class="mr-2">
                {{ getStatusText(withdrawal.status) }}
              </v-chip>
              <v-chip small :color="getStatusChipColor(withdrawal.partner_status)" :text-color="getStatusChipTextColor(withdrawal.partner_status)">
                {{ getStatusText(withdrawal.partner_status) }}
              </v-chip>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Detail card -->
      <v-card outlined class="detail-card mb-4">
        <v-card-title class="d-flex align-center">
          <v-icon color="grey darken-1" class="mr-2">mdi-information-outline</v-icon>
          Informations
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="py-4">
          <v-row>
            <v-col cols="12" md="4" class="detail-item">
              <div class="detail-label">Processeur</div>
              <div class="detail-value">{{ withdrawal.processor_name }}</div>
            </v-col>
            <v-col cols="12" md="4" class="detail-item">
              <div class="detail-label">Telephone</div>
              <div class="detail-value">{{ withdrawal.phone }}</div>
            </v-col>
            <v-col cols="12" md="4" class="detail-item">
              <div class="detail-label">Date de demande</div>
              <div class="detail-value">{{ withdrawal.created_at | formatDate }} a {{ withdrawal.created_at | formatTime }}</div>
            </v-col>
            <v-col cols="12" md="4" class="detail-item" v-if="withdrawal.validated_at">
              <div class="detail-label">Date de validation</div>
              <div class="detail-value">{{ withdrawal.validated_at | formatDate }} a {{ withdrawal.validated_at | formatTime }}</div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Payments table -->
      <v-card outlined class="detail-card">
        <v-card-title class="d-flex align-center">
          <v-icon color="grey darken-1" class="mr-2">mdi-credit-card-outline</v-icon>
          Paiements associes
          <v-spacer></v-spacer>
          <v-chip small color="blue lighten-4" text-color="blue darken-3">
            {{ withdrawal.payments ? withdrawal.payments.length : 0 }} paiement{{ withdrawal.payments && withdrawal.payments.length > 1 ? 's' : '' }}
          </v-chip>
        </v-card-title>
        <v-divider></v-divider>
        <div class="table-responsive" v-if="withdrawal.payments && withdrawal.payments.length > 0">
          <table class="report-table">
            <thead>
              <tr>
                <th class="text-left">#</th>
                <th>MONTANT</th>
                <th>PAYEUR</th>
                <th>ID. TRANSACTION</th>
                <th>STATUT</th>
                <th>DATE</th>
                <th>HEURE</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in withdrawal.payments" :key="item.id">
                <td class="text-left font-weight-bold">#{{ item.order_number }}</td>
                <td>{{ formatCurrency(item.sale_amount) }} {{ item.currency }}</td>
                <td>{{ item.payer || '--' }}</td>
                <td>{{ item.transaction_id || '--' }}</td>
                <td>
                  <v-chip x-small :color="getStatusChipColor(item.status)" :text-color="getStatusChipTextColor(item.status)">
                    {{ getPaymentStatusText(item.status) }}
                  </v-chip>
                </td>
                <td class="text-caption">{{ item.created_at | formatDate }}</td>
                <td class="text-caption">{{ item.created_at | formatTime }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <v-card-text v-else class="text-center grey--text py-6">
          Aucun paiement associe
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script>
import BreadcrumbComponent from '../../components/BreadcrumbComponent.vue'
import PreloaderComponent from '../../components/PreloaderComponent.vue'

export default {
  name: 'WithdrawalRequestDetailView',
  components: { BreadcrumbComponent, PreloaderComponent },
  data: () => ({
    items: [
      { text: 'ACCUEIL', disabled: false, href: '/' },
      { text: 'MONCASH', disabled: false, href: '/moncash/balance' },
      { text: 'RETRAITS', disabled: false, href: '/moncash/withdrawal-requests' },
    ],
    enable: false,
    withdrawal: null,
  }),
  methods: {
    formatCurrency(value) {
      const num = parseFloat(value)
      if (isNaN(num)) return '0.00'
      return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    load() {
      const self = this
      this.$axios.get('/withdrawal-requests/' + this.$route.params.id + '?include=business,payments')
        .then(function (response) {
          self.withdrawal = response.data
          self.enable = true
          self.items.push({
            text: '#' + response.data.uid,
            disabled: true,
            href: '#',
          })
        })
    },
    getStatusChipColor(status) {
      if (status === 'APPROVED' || status === 'PAID') return 'green lighten-4'
      if (status === 'PENDING') return 'blue lighten-4'
      return 'red lighten-4'
    },
    getStatusChipTextColor(status) {
      if (status === 'APPROVED' || status === 'PAID') return 'green darken-3'
      if (status === 'PENDING') return 'blue darken-3'
      return 'red darken-3'
    },
    getStatusText(status) {
      if (status === 'APPROVED') return 'Approuve'
      if (status === 'PAID') return 'Paye'
      if (status === 'PENDING') return 'En attente'
      if (status === 'CONFIRMED') return 'Confirme'
      return 'Rejete'
    },
    getPaymentStatusText(status) {
      if (status === 'CONFIRMED') return 'Confirme'
      if (status === 'PENDING') return 'En attente'
      if (status === 'CANCELLED') return 'Annule'
      return 'Refuse'
    },
  },
  mounted() {
    this.load()
  },
}
</script>

<style scoped>
.summary-card {
  border-radius: 12px !important;
  transition: box-shadow 0.2s ease;
}
.summary-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}
.summary-card--total { border-left: 4px solid #1565C0 !important; background-color: #E3F2FD !important; }
.summary-card--fees { border-left: 4px solid #C62828 !important; background-color: #FFEBEE !important; }
.summary-card--receive { border-left: 4px solid #2E7D32 !important; background-color: #E8F5E9 !important; }
.summary-card--status { border-left: 4px solid #E65100 !important; background-color: #FFF3E0 !important; }

.summary-label { font-size: 0.8rem; font-weight: 500; color: #616161; }
.summary-amount { font-size: 1.4rem; font-weight: 700; color: #212121; margin-top: 2px; }
.summary-currency { font-size: 0.75rem; color: #9E9E9E; font-weight: 500; }

.detail-card { border-radius: 12px !important; }
.detail-item { margin-bottom: 8px; }
.detail-label { font-size: 0.75rem; color: #9E9E9E; font-weight: 500; text-transform: uppercase; letter-spacing: 0.03em; }
.detail-value { font-size: 0.95rem; font-weight: 600; color: #212121; margin-top: 2px; }

.table-card { border: 1px solid #e8ecf1 !important; border-radius: 8px !important; overflow: hidden; }
.table-responsive { overflow-x: auto; }
.report-table { width: 100%; border-collapse: collapse; font-size: 0.8125rem; }
.report-table thead th {
  background: #f8fafc; font-weight: 600; font-size: 0.6875rem;
  text-transform: uppercase; letter-spacing: 0.05em; color: #64748b;
  padding: 12px; text-align: center; border-bottom: 2px solid #e8ecf1; white-space: nowrap;
}
.report-table tbody td {
  padding: 10px 12px; text-align: center; border-bottom: 1px solid #f1f5f9; white-space: nowrap;
}
.report-table tbody tr:hover { background-color: rgba(25, 65, 115, 0.03); }
</style>
